import { Coordinates } from "../../../types/user";
import { handleApiError } from "../utils/commonApiError";
import {
  nominatimApiClient,
  photonApiClient,
} from "./api/reverseGeocoding.apiClient";

class ReverseGeocodingService {
  /**
   * Fetch address from coordinates using Nominatim.
   */
  private async fetchFromNominatim(
    latitude: number,
    longitude: number
  ): Promise<string | null> {
    try {
      console.log("[ReverseGeocodingService] Calling Nominatim API...");
      const response = await nominatimApiClient.get("/reverse", {
        params: { lat: latitude, lon: longitude, format: "json" },
      });
      console.log("response.data: ", response.data);
      if (response.data.display_name) {
        console.log(
          "[ReverseGeocodingService] Nominatim response:",
          response.data.display_name
        );
        return response.data.display_name;
      }
      return null;
    } catch (error) {
      console.error("[ReverseGeocodingService] Nominatim API failed:", error);
      throw handleApiError(error);
    }
  }

  /**
   * Fetch address from coordinates using Photon.
   */
  private async fetchFromPhoton(
    latitude: number,
    longitude: number
  ): Promise<string | null> {
    try {
      console.log("[ReverseGeocodingService] Calling Photon API...");
      const response = await photonApiClient.get("/reverse", {
        params: { lat: latitude, lon: longitude },
      });
      console.log("response.data: ", response.data);
      if (response.data.features && response.data.features.length > 0) {
        const addressParts = response.data.features[0].properties;
        const address = [
          addressParts.name,
          addressParts.city || addressParts.town,
          addressParts.state,
          addressParts.country,
        ]
          .filter(Boolean)
          .join(", ");
        console.log("[ReverseGeocodingService] Photon response:", address);
        return address;
      }
      return null;
    } catch (error) {
      console.error("[ReverseGeocodingService] Photon API failed:", error);
      throw handleApiError(error);
    }
  }

  /**
   * Get address from coordinates using a fallback mechanism.
   */
  public async getAddressFromCoordinates(co: Coordinates): Promise<string> {
    const lat = co.latitude;
    const long = co.longitude;
    try {
      const nominatimAddress: string | null = await this.fetchFromNominatim(
        lat,
        long
      );
      if (nominatimAddress) {
        return nominatimAddress;
      }
    } catch (error) {
      console.warn(
        "[ReverseGeocodingService] Nominatim failed, falling back to Photon..."
      );
    }

    try {
      const photonAddress: string | null = await this.fetchFromPhoton(
        lat,
        long
      );
      if (photonAddress) {
        return photonAddress;
      }
    } catch (error) {
      console.warn("[ReverseGeocodingService] Photon also failed.");
    }

    throw new Error("Unable to fetch address from reverse geocoding services.");
  }
  public getAddressAPI = async (coordinates: Coordinates): Promise<string> => {
    let address = await this.getAddressFromCoordinates(coordinates);
    if (!address.length) {
      address = "Address not available";
    }
    return address;
  };
}

export const reverseGeocodingService = new ReverseGeocodingService();
